import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from "react";
import { analyticsEvents, analyticsTypes, apiURI, eventTypes } from "lib/utils/constants";
import {
  NonPaymentMethodType,
  PAYMENT_METHOD_MAPPINGS,
  PaymentGateways,
  PaymentMode,
  PaymentModeType,
  PaymentProcessingType,
  PaymentStatusType,
  UnApplicablePaymentReasonType,
} from "lib/types/payments";
import { ErrorType } from "lib/types/error";
import { errorToast, infoToast } from "lib/utils/toasters";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useUserContext } from "lib/contexts/UserProvider";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { useLoader } from "lib/contexts/LoaderContext";
import { useLocale } from "lib/hooks/useLocale";
import {
  handlePaymentSuccessRedirection,
  isEmptyObj,
  sleep,
  addHttpIfMissing,
  appendQueryParam,
  getAutomationPayloadForMethod,
  publishPostMessage,
} from "lib/utils/helpers";
import { getPaymentMethodAction, getPaymentStatus } from "lib/core/apiMethods";
import { initializeCfSubscription } from "lib/third-party/cashfreeV2";
import {
  initializePayment,
  loadPgSdk,
  triggerSuccessEvent,
  parsePaymentsResponse,
  handlePaymentError,
} from "lib/utils/payments";
import { getItems } from "lib/utils/checkout";
import { getGiftCardsUrl } from "lib/utils/giftCards";
import { getRequest, postRequest, putRequest } from "lib/core/apiClient";
import useInterval from "lib/hooks/useInterval";
import { mutate } from "swr";
import { resetPaytmDesktopWidth, shouldMountPaytmInline } from "lib/third-party/paytm";
import { useAddressValidation } from "lib/hooks/useAddressValidation";
import { useCheckoutErrors } from "lib/hooks/useCheckoutErrors";
import { usePopupOrchestrator } from "./PopupOrchestratorProvider";

type PaymentMethodDialogType = PaymentModeType | "PAYTM" | "NONE" | "COD_ORDER_PLACING" | "COD_OTP";

const Context = createContext<PaymentContext>({ state: {}, actions: {} } as PaymentContext);

export enum ActionType {
  SET_PAYMENT_METHODS,
  SET_PAYMENT_METHOD_DIALOG,
  SET_STATE,
}

export type Action =
  | { type: ActionType.SET_PAYMENT_METHODS; payload: PaymentMode[] }
  | { type: ActionType.SET_PAYMENT_METHOD_DIALOG; payload: PaymentMethodDialogType }
  | { type: ActionType.SET_STATE; payload: Partial<PaymentState> };

export interface PaymentContextState {
  paymentMethods: PaymentMode[];
  isProcessingDialogVisible: boolean;
  paymentStatus: PaymentStatusType;
  openPaymentMethodDialog: PaymentMethodDialogType;
  isCashfreeFullScreen: boolean;
}

export interface PaymentState extends PaymentContextState {
  isPolling: boolean;
  splittedPaymentMethods: PaymentMode[];
  isCancellable?: boolean;
  isQRCode: boolean;
}

export interface PaymentContext {
  state: PaymentState;
  actions: {
    setPaymentMethods: (values: PaymentMode[]) => void;
    handlePaymentMethodSelected: (method: PaymentModeType, context?: "SPLIT_COD") => Promise<{} | void>;
    setPaymentMethodDialog: (method: PaymentMethodDialogType) => void;
    getSplittedPaymentMethods: () => PaymentMode[];
    showPaymentProcessingDialog: ({ paymentStatus }: PaymentProcessingType) => void;
    hidePaymentProcessingDialog: () => void;
    handleNonPaymentOrderPlacement: (method: NonPaymentMethodType) => Promise<void>;
    handleUnapplicablePaymentMethod: (
      mode: PaymentModeType,
      reason: UnApplicablePaymentReasonType[],
    ) => Promise<void>;
    handleCancelPayment: () => void;
    setPaymentState: (state: Partial<PaymentState>) => void;
    mutatePayment: (shouldMutateGiftCards?: boolean) => void;
  };
}

function reducer(state: PaymentState, action: Action): PaymentState {
  switch (action.type) {
    case ActionType.SET_PAYMENT_METHODS: {
      return {
        ...state,
        paymentMethods: action.payload,
      };
    }
    case ActionType.SET_PAYMENT_METHOD_DIALOG: {
      return {
        ...state,
        openPaymentMethodDialog: action.payload,
      };
    }
    case ActionType.SET_STATE: {
      return {
        ...state,
        ...action.payload,
      };
    }
  }
  return state;
}

const PaymentInitialState: PaymentState = {
  paymentMethods: [],
  openPaymentMethodDialog: "NONE",
  isProcessingDialogVisible: false,
  paymentStatus: "DRAFT",
  isPolling: false,
  splittedPaymentMethods: [],
  isQRCode: false,
  isCancellable: true,
  isCashfreeFullScreen: false,
};

const isSubscriptionAuthorization = (response: any): boolean =>
  Boolean(response?.pg_subscription_reference || response?.subscription_status);

export const PaymentProvider: React.FC<React.PropsWithChildren<{ initialState?: PaymentState }>> = ({
  initialState = PaymentInitialState,
  children,
}) => {
  const [reducerState, dispatch] = useReducer(reducer, initialState);
  const {
    state: {
      hasDefaultShippingHandleSelected,
      checkoutId,
      billing,
      isC2P,
      originUrl,
      checkoutToken,
      checkoutValidations,
      shippingHandles,
      isFlashRedirect,
      checkoutItems,
    },
    actions: {
      setCheckoutModal,
      setOOSItems,
      setCoupons,
      updateCheckoutFromPayments,
      setCheckoutExpired,
      setActiveComponent,
    },
  } = useCheckoutContext();
  const {
    state: { checkoutMetadata, merchant },
  } = useMerchantContext();
  const {
    state: { user },
  } = useUserContext();

  const { initialCheckoutStep } = checkoutMetadata ?? {};
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const { setIsLoading } = useLoader();
  const { t } = useLocale();
  const useKratosPaymentFlow = Boolean(merchant?.migrationConfig?.kratosPaymentFlow);
  const useKratosOrderFlow = Boolean(merchant?.migrationConfig?.kratosOrderFlow);

  const { verifyDefaultAddress } = useAddressValidation();
  const { openPopup } = usePopupOrchestrator();
  const { triggerCheckoutErrorInfoToast } = useCheckoutErrors();

  //ref to control cashfree v3 dialog iframe vs redirect
  const isCfIframe = useRef<boolean | null>(null);

  const setPaymentMethods = useCallback((methods: PaymentMode[]) => {
    dispatch({
      type: ActionType.SET_PAYMENT_METHODS,
      payload: methods,
    });
  }, []);

  const setPaymentMethodDialog = useCallback((dialog: PaymentMethodDialogType = "NONE") => {
    dispatch({
      type: ActionType.SET_PAYMENT_METHOD_DIALOG,
      payload: dialog,
    });
  }, []);

  const setPaymentState = useCallback((state: Partial<PaymentState>) => {
    dispatch({
      type: ActionType.SET_STATE,
      payload: state,
    });
  }, []);

  const showProcessingDialog = useCallback(
    ({ paymentStatus }: PaymentProcessingType) => {
      setPaymentState({
        isProcessingDialogVisible: true,
        paymentStatus: paymentStatus,
      });
    },
    [setPaymentState],
  );

  const hideProcessingDialog = useCallback(() => {
    setPaymentState({
      isProcessingDialogVisible: false,
      paymentStatus: "DRAFT",
    });
  }, [setPaymentState]);

  const handlePaymentSucess = useCallback(() => {
    setPaymentState({
      paymentStatus: "PROCESSING",
      isPolling: true,
      isProcessingDialogVisible: true,
    });
  }, [setPaymentState]);

  const handlePaymentFailure = useCallback(async () => {
    setPaymentState({
      isPolling: false,
    });
    hideProcessingDialog();
    errorToast(t("payment_failed_retry"));
    publishPostMessage(eventTypes.FLO_PAYMENT_FAILED, {
      orderAmount: Number(billing.prepaid_total),
    });
  }, [hideProcessingDialog, setPaymentState, t]);

  const handleCancelPayment = useCallback(async () => {
    try {
      await postRequest(`/checkout/v2/checkout/${checkoutId}/payments/cancel`, {});

      hideProcessingDialog();
      setPaymentMethodDialog("NONE");
      setPaymentState({
        isPolling: false,
      });
    } catch (error) {
      errorToast(t("payment_cancellation_failed"));
    }
  }, [checkoutId, hideProcessingDialog, setPaymentMethodDialog, setPaymentState, t]);

  const handlePaymentMethodSelected = useCallback(
    async (method: PaymentModeType, context?: "SPLIT_COD") => {
      if (!method) return;

      const hasDefaultHandle = hasDefaultShippingHandleSelected;

      if (!Boolean(hasDefaultHandle)) {
        openPopup("SHIPPING_HANDLES", {});
        return;
      }

      const methodData = reducerState.paymentMethods?.find((e: PaymentMode) => e.name === method);
      if (!methodData || isEmptyObj(methodData)) return;

      //TODO handle group
      if (!methodData.isAvailable) return;

      if (method !== "UPI_INTENT" && method !== "UPI_COLLECT") {
        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_PAYMENT_METHOD_SELECTED,
          eventFor: [analyticsTypes.SF_ANALYTICS, analyticsTypes.FACEBOOK_PIXEL],
          eventType: "click",
          metaData: {
            methodType: method,
            automationData: getAutomationPayloadForMethod(methodData),
          },
        });
      }

      const specialPaymentMethods = Object.keys(PAYMENT_METHOD_MAPPINGS);

      if (specialPaymentMethods.includes(method)) {
        const dialogType = PAYMENT_METHOD_MAPPINGS[method as keyof typeof PAYMENT_METHOD_MAPPINGS];
        setPaymentMethodDialog(dialogType);
        return;
      }

      //Handling Split COD Payment mode
      if (method === "SPLIT_COD") {
        try {
          setIsLoading(true);
          const paymentResponse = await getPaymentMethodAction({
            method: "SPLIT_COD",
            checkoutId,
            setCheckoutExpired,
          });
          const { modes: parsedModes } = parsePaymentsResponse(paymentResponse);
          parsedModes?.forEach((method: PaymentMode) => {
            method.context = "SPLIT_COD";
          });

          setPaymentState({
            splittedPaymentMethods: parsedModes?.filter((method: PaymentMode) => method.isAvailable),
            openPaymentMethodDialog: "SPLIT_COD",
          });
        } catch (e) {
          console.error(e);
        } finally {
          setIsLoading(false);
        }
        return;
      }

      //Handling other payment methods
      try {
        setIsLoading(true);
        const paymentResponse: any = await getPaymentMethodAction({
          method,
          checkoutId,
          context,
          additionalPayload: undefined,
          useKratosPaymentFlow,
          setCheckoutExpired,
        });
        isCfIframe.current = paymentResponse?.iframe === true && paymentResponse?.pg === "CF";

        if (isSubscriptionAuthorization(paymentResponse)) {
          const subsSessionId = paymentResponse?.next_steps?.pg_action?.action_url;

          if (Boolean(subsSessionId)) {
            await loadPgSdk({
              pg: "CF",
              pgImpl: "DEFAULT",
              pgKey: paymentResponse?.pg_key,
              pgVersion: paymentResponse?.gateway_version,
              pgAccountId: paymentResponse?.pg_account_id,
              isCFIframe: true,
            });

            setIsLoading(false);
            handlePaymentSucess();
            await initializeCfSubscription({
              subsSessionId,
              redirectTarget: paymentResponse?.next_steps?.pg_action?.action === "redirect" ? "_self" : "_blank",
            });
            return;
          }
        }

        if (paymentResponse?.implementation === "DEFAULT") {
          await loadPgSdk({
            pg: paymentResponse?.pg,
            pgImpl: paymentResponse?.implementation,
            pgKey: paymentResponse?.pg_key,
            pgVersion: paymentResponse?.gateway_version,
            pgAccountId: paymentResponse?.pg_account_id,
            isCFIframe: isCfIframe.current,
          });

          const hasRedirection = paymentResponse?.next_steps?.pg_action?.action === "redirect" ? true : false;
          const redirectUrl = hasRedirection || isCfIframe.current ? paymentResponse?.ui_callback_url : "";
          if ((paymentResponse.pg as PaymentGateways) === "CF") {
            setPaymentMethodDialog("CF");
            setPaymentState({ isCashfreeFullScreen: isCfIframe.current });
            await sleep(50);
          }
          const isPaytmInline = (paymentResponse.pg as PaymentGateways) === "PAYTM" && shouldMountPaytmInline();
          if (isPaytmInline) {
            setPaymentMethodDialog("PAYTM");
            await sleep(50);
          }

          setIsLoading(false);

          await initializePayment({
            amount: billing.prepaid_total ?? 0,
            pgOrderId: paymentResponse.pg_order_reference,
            pg: paymentResponse.pg as PaymentGateways,
            pgImpl: "DEFAULT",
            pgRedirectUrl: paymentResponse.next_steps?.pg_action?.action_url,
            pgAction: paymentResponse.next_steps?.pg_action?.action,
            paymentMode: paymentResponse.mode as PaymentModeType,
            pgAccountId: paymentResponse.pg_account_id,
            onSuccessHandler: handlePaymentSucess,
            onFailureHandler: handlePaymentFailure,
            onCancelHandler: handleCancelPayment,
            checkoutId,
            merchantId: merchant?.merchantId,
            merchantName: merchant?.displayName,
            brandLogoUrl: merchant?.logoUrl,
            brandThemeColor: merchant?.colorPallet?.primaryColor,
            userName: user?.name ?? "",
            userPhone: user?.phone ?? "",
            userEmail: user?.email ?? "",
            userId: paymentResponse?.pg_customer_id ?? "",
            redirectUrl: redirectUrl,
            floOrderReference: paymentResponse?.flo_order_reference,
            pgVersion: paymentResponse?.gateway_version,
            pgData: paymentResponse?.pg_data,
            shouldRenderIframe: paymentResponse?.iframe,
          });
          return;
        }
        //handle custom case
        setPaymentMethodDialog(method);
      } catch (e: any) {
        handlePaymentError({
          error: e,
          callbacks: {
            showErrorToast: errorToast,
            getTranslation: t,
            setOOSItems: (items) => setOOSItems(getItems(items, true) ?? []),
            setCheckoutModal,
            openPopup,
            onOutOfStock: (items) =>
              sendAnalyticsEvent({
                eventName: analyticsEvents.FLO_ITEM_OOS,
                eventFor: [analyticsTypes.SF_ANALYTICS],
                eventType: "click",
                metaData: {
                  outOfStockItems: items,
                },
              }),
          },
          payload: {
            checkoutId,
            payment_mode: method,
            ...(context && { payment_context: context }),
          },
        });
      } finally {
        setIsLoading(false);
        if (isCfIframe.current === true) {
          setPaymentMethodDialog("NONE");
          isCfIframe.current = null;
        }
      }

      return {};
    },
    [
      billing.prepaid_total,
      checkoutId,
      handlePaymentFailure,
      handlePaymentSucess,
      hasDefaultShippingHandleSelected,
      merchant?.colorPallet?.primaryColor,
      merchant?.displayName,
      merchant?.logoUrl,
      merchant?.merchantId,
      merchant?.migrationConfig?.kratosPaymentFlow,
      reducerState.paymentMethods,
      sendAnalyticsEvent,
      openPopup,
      setCheckoutModal,
      setIsLoading,
      setOOSItems,
      setPaymentMethodDialog,
      setPaymentState,
      user?.email,
      user?.name,
      user?.phone,
    ],
  );

  const getSplittedPaymentMethods = useCallback(
    () => reducerState.splittedPaymentMethods,
    [reducerState.splittedPaymentMethods],
  );

  const handleNonPaymentOrderPlacement = useCallback(
    async (method: NonPaymentMethodType) => {
      let nonPaymentMethod = "free-online";

      const isAddressValid = verifyDefaultAddress();

      if (!Boolean(user?.addresses?.length) && initialCheckoutStep === "PAYMENTS") {
        openPopup("ADDRESS_NEW", {});
        return;
      }

      if (initialCheckoutStep === "PAYMENTS" && !isAddressValid) {
        if (!Boolean(checkoutValidations?.address?.metadata)) {
          openPopup("ADDRESS_LIST", {});
          errorToast(t("serviceability_error"));
          return;
        }
        openPopup("ADDRESS_EDIT", { addressId: checkoutValidations?.address?.metadata?.addressId ?? "" });
        return;
      }

      if (!Boolean(shippingHandles?.length) && initialCheckoutStep === "PAYMENTS") {
        triggerCheckoutErrorInfoToast(t("serviceability_error"));
        setActiveComponent("SHIPPING_ADDRESS_SECTION");
        return;
      }

      if (!Boolean(hasDefaultShippingHandleSelected)) {
        openPopup("SHIPPING_HANDLES", {});
        return;
      }

      if (!user?.addresses || user?.addresses?.length === 0) {
        openPopup("ADDRESS_NEW", {});
        return;
      }

      if (method === "COD") {
        nonPaymentMethod = "cod";
        setPaymentMethodDialog("NONE");
      }
      if (method === "OFFLINE_PAYMENT") {
        nonPaymentMethod = "offline-payment";
        setPaymentMethodDialog("NONE");
      }

      const kratosPathMap: Record<string, string> = {
        "free-online": "zero",
        cod: "cod",
        "offline-payment": "offline",
      };
      const kratosPath = kratosPathMap[nonPaymentMethod];

      const apiUrl = useKratosOrderFlow
        ? `/checkout/${checkoutId}/order/${kratosPath}`
        : `/checkout/v1/checkout/${checkoutId}/${nonPaymentMethod}`;

      try {
        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_PAYMENT_MODE_SELECTED,
          eventType: "click",
          metaData: {
            modeType: method,
            methodType: method,
          },
        });
        setPaymentState({
          paymentStatus: "PROCESSING",
          isProcessingDialogVisible: true,
          // since this method handles only non-payment methods, we can set isCancellable to true
          // but I want to explicit here to make sure it's only handled for non-payment methods - removes any confusion
          isCancellable: !["cod", "offline-payment", "free-online"].includes(nonPaymentMethod),
        });

        const response = await postRequest(apiUrl, {}, useKratosOrderFlow ? "KRATOS_PRIVATE" : "CHECKOUT");
        const status = response?.status;
        const orderStatusUrl = useKratosOrderFlow
          ? response?.order_status_url
          : response?.order?.platform_order?.data?.order_status_url;
        const orderAmount = useKratosOrderFlow ? Number(response?.order_amount) : Number(response?.order?.amount);

        let redirectUrl =
          Boolean(orderStatusUrl) && !merchant?.forceCustomThankYouPage
            ? orderStatusUrl
            : addHttpIfMissing(
                `${originUrl}/pages/order-status?tokenId=${checkoutToken}&checkoutId=${checkoutId}`,
              );

        if (Boolean(isFlashRedirect)) {
          redirectUrl = appendQueryParam(orderStatusUrl, "checkoutId", checkoutId ?? "");
        }

        if (redirectUrl && status === "COMPLETED") {
          sendAnalyticsEvent({
            eventName: analyticsEvents.FLO_PAYMENT_COMPLETED,
            eventFor: [analyticsTypes.SF_ANALYTICS, ...(!isC2P ? [analyticsTypes.FACEBOOK_PIXEL] : [])],
            eventType: "click",
            metaData: {
              orderAmount,
            },
          });
          publishPostMessage(eventTypes.FLO_PAYMENT_COMPLETED, {
            method: method,
            orderAmount,
          });
          handlePaymentSuccessRedirection(redirectUrl);
          return;
        }
        if (status === "ORDER_FAILED") {
          setPaymentState({
            paymentStatus: "ORDER_FAILED",
          });
          sendAnalyticsEvent({
            eventName: analyticsEvents.FLO_ITEM_OOS,
            eventFor: [analyticsTypes.SF_ANALYTICS],
            eventType: "click",
            metaData: {
              outOfStockItems: checkoutItems,
            },
          });
          return;
        }
        setPaymentState({
          isProcessingDialogVisible: false,
        });
      } catch (err: any) {
        console.error(err);
        setPaymentState({
          isProcessingDialogVisible: false,
        });
        handlePaymentError({
          error: err,
          callbacks: {
            showErrorToast: errorToast,
            getTranslation: t,
            setOOSItems: (items) => setOOSItems(getItems(items, true) ?? []),
            setCheckoutModal,
            openPopup,
            onOutOfStock: (items) =>
              sendAnalyticsEvent({
                eventName: analyticsEvents.FLO_ITEM_OOS,
                eventFor: [analyticsTypes.SF_ANALYTICS],
                eventType: "click",
                metaData: {
                  outOfStockItems: items,
                },
              }),
          },
          payload: {
            checkoutId,
            payment_mode: method,
          },
          options: {
            fallbackMessageKey: "order_place_failed",
            errorUrl: apiUrl,
            errorType: ErrorType.ORDER_PLACEMENT_FAILED,
          },
        });
      }
    },
    [
      checkoutId,
      checkoutItems,
      isC2P,
      sendAnalyticsEvent,
      setCheckoutModal,
      setOOSItems,
      setPaymentMethodDialog,
      setPaymentState,
      useKratosOrderFlow,
      t,
    ],
  );

  const handleUnapplicablePaymentMethod = useCallback(
    async (mode: PaymentModeType, reason: UnApplicablePaymentReasonType[]) => {
      if (!mode || !Boolean(reason?.length)) return;
      try {
        setIsLoading(true);
        const useKratosApi = Boolean(merchant?.migrationConfig?.kratosOrderFlow);
        const enableUrl = useKratosApi
          ? `/checkout/${checkoutId}/payment-modes/${mode}/enable`
          : `/checkout/v1/checkout/${checkoutId}/payment-modes/${mode}/enable`;
        const response: any = await putRequest(enableUrl, {}, useKratosApi ? "KRATOS_PRIVATE" : "CHECKOUT");

        const discountResponse = await getRequest(`/checkout/${checkoutId}/discount`, "KRATOS_PRIVATE");
        setCoupons(discountResponse);
        updateCheckoutFromPayments(response);

        // incase of one page or multiple page mutate the payments as we have
        // just enabled it and don't wait for shipping handles
        // in the updateCheckoutFromPayments
        mutatePayment();
        mutate(`/checkout/${checkoutId}/rewards`);
        mutate(`/checkout/${checkoutId}/other-offers`);
        if (
          initialCheckoutStep === "PAYMENTS" &&
          Boolean(response?.metadata?.show_shipping_handle_selector)
        ) {
          setActiveComponent("SHIPPING_HANDLES_SECTION");
        }

        infoToast(t("order_updated_toaster"));
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    },
    [checkoutId, setCoupons, setIsLoading, t, updateCheckoutFromPayments],
  );

  const pollPaymentStatus = useCallback(async () => {
    try {
      const paymentStatus: any = await getPaymentStatus(checkoutId);
      if (paymentStatus?.status === "ABANDONED") {
        setPaymentState({
          isPolling: false,
        });
        return;
      }
      if (paymentStatus?.status === "PAYMENT_FAILED") {
        setPaymentState({
          isPolling: false,
          paymentStatus: "PAYMENT_FAILED",
        });
        return;
      }
      if (paymentStatus?.status === "PAYMENT_COMPLETED") {
        setPaymentState({
          paymentStatus: "PLACING_ORDER",
        });
        triggerSuccessEvent();
        return;
      }
      if (paymentStatus?.status === "COMPLETED") {
        setPaymentState({
          isPolling: false,
        });
        let redirectUrl =
          Boolean(paymentStatus?.order_status_url) && !merchant?.forceCustomThankYouPage
            ? paymentStatus?.order_status_url
            : addHttpIfMissing(
                `${originUrl}/pages/order-status?tokenId=${checkoutToken}&checkoutId=${checkoutId}`,
              );

        if (Boolean(isFlashRedirect)) {
          redirectUrl = appendQueryParam(paymentStatus?.order_status_url, "checkoutId", checkoutId ?? "");
        }

        triggerSuccessEvent();
        if (typeof window !== "undefined" && window.localStorage.getItem("payment_success_flag")) {
          localStorage.removeItem("payment_success_flag");
        }

        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_PAYMENT_COMPLETED,
          eventFor: [analyticsTypes.SF_ANALYTICS, ...(!isC2P ? [analyticsTypes.FACEBOOK_PIXEL] : [])],
          eventType: "click",
          metaData: {
            orderAmount: Number(paymentStatus?.order_amount),
          },
        });

        publishPostMessage(eventTypes.FLO_PAYMENT_COMPLETED, {
          method: "ONLINE",
          orderAmount: Number(paymentStatus?.order_amount),
        });

        if (redirectUrl) {
          handlePaymentSuccessRedirection(redirectUrl);
        }
        return;
      }

      if (paymentStatus?.status === "ORDER_FAILED") {
        setPaymentState({
          isPolling: false,
          paymentStatus: "ORDER_FAILED",
        });
        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_ITEM_OOS,
          eventFor: [analyticsTypes.SF_ANALYTICS],
          eventType: "click",
          metaData: {
            outOfStockItems: checkoutItems,
          },
        });
        return;
      }
    } catch (e) {
      console.error(e);
    }
  }, [checkoutId, checkoutItems, isC2P, sendAnalyticsEvent, setPaymentState]);

  const mutatePayment = useCallback((shouldMutateGiftCards: boolean = true) => {
      if (shouldMutateGiftCards) {
        mutate(getGiftCardsUrl(checkoutId));
      }

      mutate(`/checkout/${checkoutId}/payments`);
      setPaymentState({
        isPolling: false,
      });

      clearIntervalTimer();
  }, [checkoutId, setPaymentState]);

  const { clearIntervalTimer } = useInterval(pollPaymentStatus, reducerState.isPolling ? 3000 : null);

  // Paytm is shown a desktop window width while its dialog is open; give the real width back once it closes.
  useEffect(() => {
    if (reducerState.openPaymentMethodDialog !== "PAYTM") resetPaytmDesktopWidth();
  }, [reducerState.openPaymentMethodDialog]);

  const actions = useMemo(
    () => ({
      setPaymentMethods,
      setPaymentMethodDialog,
      setPaymentState,
      handlePaymentMethodSelected,
      getSplittedPaymentMethods,
      showPaymentProcessingDialog: showProcessingDialog,
      hidePaymentProcessingDialog: hideProcessingDialog,
      handleNonPaymentOrderPlacement,
      handleUnapplicablePaymentMethod,
      handleCancelPayment,
      mutatePayment,
    }),
    [
      getSplittedPaymentMethods,
      handleCancelPayment,
      handleNonPaymentOrderPlacement,
      handlePaymentMethodSelected,
      handleUnapplicablePaymentMethod,
      hideProcessingDialog,
      setPaymentMethodDialog,
      setPaymentMethods,
      setPaymentState,
      showProcessingDialog,
      mutatePayment,
    ],
  );

  return (
    <Context.Provider
      value={{
        state: reducerState,
        actions,
      }}>
      {children}
    </Context.Provider>
  );
};

export function usePaymentContext() {
  if (!Boolean(Context)) throw new Error("usePaymentContext must be used within a PaymentProvider");
  const PaymentContext = useContext(Context);
  return PaymentContext as PaymentContext;
}
